/**
 * Invitation Required Page
 * Shown to users who are not in the alpha whitelist
 */

import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../features/auth/AuthProvider';
import { UnauthorizedUserScreen } from '../components/auth/UnauthorizedUserScreen';

interface UnauthorizedUser {
  id: string;
  email: string;
  displayName: string;
  spotifyUrl: string;
}

export function InvitationRequiredPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  // Try to get user data from navigation state or sessionStorage
  const user: UnauthorizedUser | null =
    location.state?.user ||
    (() => {
      const stored = sessionStorage.getItem('unauthorized_user');
      return stored ? JSON.parse(stored) : null;
    })();

  // If we have user data, show the detailed screen
  if (user) {
    // Clean up sessionStorage after retrieving
    sessionStorage.removeItem('unauthorized_user');
    return <UnauthorizedUserScreen user={user} />;
  }

  // Fallback: Keep existing generic UI for direct navigation
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {/* Logo */}
        <div className="mb-8">
          <h1 className="text-6xl font-bold text-white mb-2">Splister</h1>
          <p className="text-gray-400 text-lg">
            Smart Playlist Management for Spotify
          </p>
        </div>

        {/* Message */}
        <div className="bg-gray-800 rounded-lg p-8 shadow-2xl">
          <div className="mb-6">
            <svg
              className="mx-auto h-16 w-16 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>

          <h2 className="text-2xl font-bold text-white mb-4">
            Thank you for your interest!
          </h2>

          <p className="text-gray-300 mb-6">
            Splister is currently in alpha testing with limited access.
          </p>

          <div className="bg-gray-900 rounded-lg p-4 mb-6">
            <p className="text-gray-400 text-sm mb-2">
              If you want an invitation, please contact:
            </p>
            <a
              href="mailto:mhavo@iki.fi"
              className="text-green-500 hover:text-green-400 font-medium transition-colors"
            >
              mhavo@iki.fi
            </a>
          </div>

          <button
            onClick={handleLogout}
            className="w-full bg-gray-700 hover:bg-gray-600 text-white font-medium py-3 px-6 rounded-lg transition-colors"
          >
            Back to Login
          </button>
        </div>

        {/* Info */}
        <div className="mt-8 text-gray-500 text-sm">
          <p>We're working hard to bring Splister to everyone soon!</p>
        </div>
      </div>
    </div>
  );
}
